import { Link } from "react-router-dom";
import {
  Gift, Share2, UserPlus, CreditCard, CheckCircle2, XCircle, ArrowUpRight, Clock3, BookOpen,
} from "lucide-react";
import SEO from "@/components/SEO";
import DashboardHeader from "@/components/DashboardHeader";
import DashboardFooter from "@/components/DashboardFooter";
import BackToDashboard from "@/components/BackToDashboard";
import { useOptionalMerchant, isOwner } from "@/lib/useMerchant";

/**
 * ReferralGuide.jsx -- public help page (like TeamGuide): owners and visitors can read it.
 * Amounts below are approximate and based on MONTHLY list prices (price / 30 x 10 days).
 */

const EARNINGS = [
  { plan: "Creator", price: "₹499", ten: "≈ ₹170", thirty: "≈ ₹500" },
  { plan: "Starter", price: "₹1,499", ten: "≈ ₹500", thirty: "≈ ₹1,500" },
  { plan: "Growth", price: "₹2,899", ten: "≈ ₹1,000", thirty: "≈ ₹2,900" },
  { plan: "Pro", price: "₹6,499", ten: "≈ ₹2,200", thirty: "≈ ₹6,500" },
];

const FAQ = [
  {
    q: "When exactly do I get my days?",
    a: "The moment the merchant you invited pays for their first plan. It's automatic: your expiry date moves forward and you get a notification. Signing up alone earns nothing.",
  },
  {
    q: "Does it matter whether I'm on a monthly or yearly plan?",
    a: "No. You always get exactly 10 days per successful referral on whatever plan you're on. The rupee value is just higher on a bigger plan, because a day of Pro is worth more than a day of Starter. On a yearly plan a day costs a bit less than on monthly, so the value is somewhat lower than the table, but it's still 10 days.",
  },
  {
    q: "I've already paid for a plan change that starts later. What happens?",
    a: "Your current plan is extended by 10 days and the plan you've queued starts 10 days later, still running for its full paid period. Nothing you paid for is lost.",
  },
  {
    q: "Is there a limit on how many people I can refer?",
    a: "No limit. 3 successful referrals give you 30 days, 6 give you 60, and so on.",
  },
  {
    q: "What if the person I invited asks for a refund?",
    a: "All Checkout DM payments are non-refundable, so a qualified referral is never reversed.",
  },
  {
    q: "What if my plan expires before my friend pays?",
    a: "Days are only added to an active paid plan. If your plan has already expired when they pay, that referral doesn't earn days, so renew before you share your link widely.",
  },
  {
    q: "How long does my invite link stay valid after someone clicks it?",
    a: "90 days. When someone opens your link, their browser remembers your invite for 90 days. If they sign up through Instagram within that time on the same browser and device, your invite is saved. If they wait longer, clear their browser data, or sign up on another device or browser, the invite can be lost.",
  },
  {
    q: "What if the person I invited signs up but doesn't register right away?",
    a: "That's fine as long as they sign up within 90 days of clicking your link, using the same browser and device. Once they register, your invite is saved to their account.",
  },
  {
    q: "My friend signed up on the Free plan first and upgraded later. Do I still earn?",
    a: "Yes, as long as their account still exists. Your invite is saved when they sign up, and you earn 10 days when they make their first payment. Free accounts with no activity for 30 days are deleted, and the invite is lost with them, so it works best when your friend upgrades while still using their account.",
  },
  {
    q: "What if my friend's link didn't get recorded?",
    a: "Contact us with your friend's Instagram username and we'll check it.",
  },
  {
    q: "Which referrals don't count?",
    a: "Merchants who already pay or have paid before, signing up with your own code or an account using the same email as yours, and payments of zero (for example a 100%-off coupon). Only real money received counts.",
  },
];

function Step({ n, icon: Icon, title, desc }) {
  return (
    <div className="rounded-2xl border border-ink/10 bg-white p-5">
      <div className="flex items-start gap-3.5">
        <span className="grid h-10 w-10 flex-none place-items-center rounded-2xl bg-hotpink font-display text-sm font-black text-white">
          {n}
        </span>
        <div>
          <div className="flex items-center gap-2">
            <Icon size={15} className="text-ink/50" />
            <p className="font-bold text-ink">{title}</p>
          </div>
          <p className="mt-1.5 text-[13px] leading-relaxed text-ink/70">{desc}</p>
        </div>
      </div>
    </div>
  );
}

export default function ReferralGuide() {
  const { me } = useOptionalMerchant();
  const owner = isOwner(me);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: "Referral Program Guide",
    description: "Invite merchants to Checkout DM and earn 10 extra days on your plan for every merchant who subscribes.",
    publisher: { "@type": "Organization", name: "Checkout DM", url: "https://checkoutdm.com" },
    mainEntityOfPage: "https://checkoutdm.com/referral-guide",
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Referral Program Guide | Checkout DM"
        description="Invite merchants to Checkout DM and earn 10 extra days on your plan for every merchant who subscribes. Here's exactly how it works and what you can earn."
        path="/referral-guide"
        structuredData={[articleSchema, faqSchema]}
      />
      <DashboardHeader me={me} />

      <main className="mx-auto max-w-4xl px-6 pt-10 md:px-10">
        {me && <BackToDashboard />}

        <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Help</p>
        <h1 className="mt-2 font-display text-4xl font-black tracking-tighter md:text-5xl">
          Refer merchants, earn free days
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ink/70 md:text-base">
          Every merchant you bring who subscribes to a paid plan adds <strong>10 days</strong> to your
          current plan. No codes to redeem, no forms. It's applied automatically.
        </p>
        <p className="mt-3 text-sm">
          <Link to="/pricing" className="inline-flex items-center gap-1.5 font-semibold text-hotpink hover:underline">
            <BookOpen size={13} /> See plans and pricing →
          </Link>
        </p>

        <section className="mt-8 rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8">
          <div className="flex items-center gap-3.5">
            <span className="grid h-12 w-12 flex-none place-items-center rounded-2xl bg-hotpink/10 text-hotpink">
              <Gift size={20} strokeWidth={2.2} />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-ink/50">How it works</p>
              <h2 className="mt-0.5 font-display text-2xl font-black tracking-tight md:text-3xl">Three steps</h2>
            </div>
          </div>
          <div className="mt-6 grid gap-3 md:grid-cols-3">
            <Step n={1} icon={Share2} title="Share your link"
              desc="Copy your personal invite link from Billing → Refer & earn and send it to another Instagram seller." />
            <Step n={2} icon={UserPlus} title="They sign up"
              desc="They connect their Instagram through your link. Their invite is saved automatically." />
            <Step n={3} icon={CreditCard} title="They subscribe, you earn"
              desc="When their first payment goes through, 10 days are added to your plan and you get a notification." />
          </div>
        </section>

        <section className="mt-6 rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-ink/50">What you can earn</p>
          <h2 className="mt-0.5 font-display text-2xl font-black tracking-tight md:text-3xl">
            10 days of your own plan, per referral
          </h2>
          <p className="mt-2 text-sm text-ink/60">
            The days are the same for everyone. What they're worth depends on your plan. Approximate values at
            current monthly prices (in your own currency it's the same 10 days of your plan):
          </p>
          <div className="mt-5 overflow-x-auto">
            <table className="w-full min-w-[420px] text-left text-sm">
              <thead>
                <tr className="text-[11px] font-bold uppercase tracking-wide text-ink/50">
                  <th className="pb-2 pr-3">Your plan</th>
                  <th className="pb-2 pr-3">Monthly price</th>
                  <th className="pb-2 pr-3">1 referral (10 days)</th>
                  <th className="pb-2">3 referrals (30 days)</th>
                </tr>
              </thead>
              <tbody>
                {EARNINGS.map((r) => (
                  <tr key={r.plan} className="border-t border-ink/10">
                    <td className="py-3 pr-3 font-semibold text-ink">{r.plan}</td>
                    <td className="py-3 pr-3 text-ink/70">{r.price}</td>
                    <td className="py-3 pr-3 font-semibold text-hotpink">{r.ten}</td>
                    <td className="py-3 font-semibold text-hotpink">{r.thirty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-ink/50">
            Values are estimates to show scale, not cash. Rewards are days added to your plan, and there's no cap on
            how many you can earn.
          </p>
        </section>

        <section className="mt-6 grid gap-6 md:grid-cols-2">
          <div className="rounded-[2rem] border border-ink/10 bg-white p-6">
            <p className="font-display text-lg font-black tracking-tight">What counts</p>
            <ul className="mt-3 space-y-2 text-sm text-ink/70">
              {[
                "A new merchant who signs up through your link",
                "Who then pays for any paid plan, monthly or yearly",
                "While your own paid plan is active",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <CheckCircle2 size={15} className="mt-0.5 flex-none text-emerald-600" /> {t}
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-[2rem] border border-ink/10 bg-white p-6">
            <p className="font-display text-lg font-black tracking-tight">What doesn't</p>
            <ul className="mt-3 space-y-2 text-sm text-ink/70">
              {[
                "Sign-ups that stay on the Free plan",
                "Merchants who already paid us before",
                "Your own account, or one using your email",
                "Zero-amount payments, such as a 100%-off coupon",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2">
                  <XCircle size={15} className="mt-0.5 flex-none text-ink/40" /> {t}
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="mt-6 rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8">
          <h2 className="font-display text-2xl font-black tracking-tight">Common questions</h2>
          <div className="mt-5 space-y-5">
            {FAQ.map((f) => (
              <div key={f.q}>
                <p className="font-semibold text-ink">{f.q}</p>
                <p className="mt-1 text-sm leading-relaxed text-ink/60">{f.a}</p>
              </div>
            ))}
          </div>
          {owner && (
            <Link
              to="/billing"
              className="cta-glow mt-6 inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-5 py-2.5 text-sm font-semibold text-white"
            >
              Get my invite link <ArrowUpRight size={14} />
            </Link>
          )}
        </section>

        <div className="mt-8 flex items-start gap-3 rounded-[2rem] border border-hotpink/20 bg-hotpink/5 p-6 md:p-8">
          <span className="grid h-10 w-10 flex-none place-items-center rounded-2xl bg-hotpink/10 text-hotpink">
            <Clock3 size={17} />
          </span>
          <p className="max-w-xl text-sm text-ink/70">
            Something about a referral doesn't look right? Contact us and we'll check it. Every referral and reward is
            recorded, so we can see exactly what happened.
          </p>
        </div>
      </main>

      <div className="mt-10">
        <DashboardFooter />
      </div>
    </div>
  );
}
